import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { Icon } from '/icons.js';
import { get } from '/api.js';
import { SEEN } from '/notify_history.js';
import { KeepPage } from '/shortcuts.js';

/**
 * The chrome: account, wordmark, cog, and the four tabs.
 *
 * The tabs were LEAGUE / TEAM / LEADERS / SETTINGS in the mockup. Cueforge has
 * circles rather than teams, and no divisions or fixtures, so Team became
 * Circle and everything behind every tab is backed by an endpoint that exists.
 *
 * Three later corrections, each because a label disagreed with its screen:
 * League showed your own dashboard, so it is Home; you belong to more than one
 * circle, so it is Circles; and Settings is somewhere you go occasionally, not
 * a quarter of the app — it moved to the account icon, which freed the slot for
 * Venues. Venues being a tab is also why device enrolment can now be found:
 * it used to be three levels down behind a conditional Settings row.
 *
 * Still four. Adding a fifth would mean inventing the data under it.
 */

export const TABS = [
  { screen: 'home', label: 'Home', icon: 'home' },
  { screen: 'circle', label: 'Circles', icon: 'users' },
  { screen: 'leagues', label: 'Leagues', icon: 'trophy' },
  { screen: 'venue', label: 'Venues', icon: 'map-pin' },
];

// There is no second row any more. It held Leagues plus three named
// placeholders — ph1-ph3 — drawn as plainly unavailable so the shape of the
// row could be seen before the things under them existed. Leagues has since
// become the largest part of the app, so it took the slot Leaders had, and
// the placeholders went with the row: a disabled button teaches nobody
// anything once the thing it stood for has arrived somewhere else.
//
// The Leaderboard did not go with it. It is still a screen, reached from the
// button on Home — which is where somebody looks for their own numbers, and
// is why it did not need a quarter of the chrome.

/**
 * How many notifications have arrived since this person last looked.
 *
 * GitHub issue #49. POLLED, and slowly. The obvious alternative is to have
 * the service worker post a message when it shows a notification, and it does
 * not cover the case this exists FOR: somebody who declined permission, or
 * whose phone was off, gets no push and so would get no bell either — which
 * is precisely the person with things to catch up on.
 *
 * Sixty seconds because a bell is not a scoreboard. The tab is often open all
 * evening, and a count that lags a minute is invisible; a poll that did not
 * lag would be sixty times the requests to say the same thing.
 */
function useUnseen(screen) {
  const [unseen, setUnseen] = useState(0);
  useEffect(() => {
    let alive = true;
    const read = () => get('/api/notifications')
      .then((n) => { if (alive) setUnseen(n.unseen || 0); })
      .catch(() => { /* a bell is not worth an error on screen */ });
    read();
    const t = setInterval(read, 60000);
    // Zero it the moment the list says it has been read, rather than waiting
    // out the poll: the bell was still counting something the person was
    // looking at, which reads as broken however briefly it lasts.
    const seen = () => { if (alive) setUnseen(0); };
    window.addEventListener(SEEN, seen);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener(SEEN, seen);
    };
    // Re-read on navigation too, so opening the list and coming back clears
    // the count without waiting out the interval.
  }, [screen]);
  return unseen;
}

export function Shell({ screen, where, onGo, children }) {
  const unseen = useUnseen(screen);
  return html`
    <div class="brand">
      <div>
        ${/* The wordmark and the stage it is at, STACKED -- issue #85.
              These sat on one line, and the comment here argued they had to:
              a second row "would push the who-is-signed-in line down and
              read as part of the address". That risk is real, and the CSS
              answers it by GROUPING rather than by ordering -- the pill sits
              tight under the wordmark while `.where` is pushed away from it,
              so the gaps say which line belongs to which. */''}
        <div class="wordmark-row">
          <span class="wordmark gold">CUEFORGE</span>
          <span class="stage" data-testid="brand-stage">alpha</span>
        </div>
        <div class="where" data-testid="home-greeting">${where}</div>
      </div>
      <!-- One control, not two. Adding a second beside the wordmark meant a
           third child in a space-between lockup and a button with no style of
           its own, which rendered as the full-width chalk one. So the fix for
           "nobody can find settings" is not another button — it is this one
           wearing the right icon. A cue ball says "you"; a cog says where
           settings and administration live. The icon gets its own colour
           because the ball's light gradient would otherwise swallow the
           inherited ivory stroke; size and position stay untouched, since the
           lockup depends on the ball's own dimensions. -->
      ${/* TWO controls now, and the warning above still stands — the fix is
            that they are WRAPPED. `.brand` is a `space-between` lockup and
            depends on having exactly two children; a third would space the
            wordmark, the bell and the cog evenly across the row and pull the
            name off the left edge. So the buttons share one box, and the
            lockup still sees two. The bell is a `.ball` like the cog rather
            than a bare button, which is the other half of what went wrong
            last time. */''}
      <div class="brand-controls">
        ${/* GitHub issue #67. Beside the bell because that is where Ben
              asked for it -- "next to the notification history" -- and the
              bell IS the notification history. A third BALL in this box, not
              a third child of `.brand`: the lockup still sees two, which is
              the rule the comment above is about. */''}
        <button class="ball" data-testid="brand-support"
                style="color:var(--bg)" title="Ask for help"
                aria-label="Ask for help"
                onClick=${() => onGo('support')}>
          <${Icon} name="mail" size=${16} />
        </button>
        <button class="ball" data-testid="brand-bell" style="color:var(--bg)"
                title=${unseen ? `${unseen} new` : 'Notifications'}
                aria-label=${unseen ? `Notifications, ${unseen} new`
                                    : 'Notifications'}
                onClick=${() => onGo('notifications')}>
          <${Icon} name="bell" size=${16} />
          ${/* The count rides ON the ball rather than beside it: a number in
                the lockup would be a third thing competing with the wordmark,
                and this is the shape every phone already reads as unread. */''}
          ${unseen > 0 && html`
            <span class="dot" data-testid="brand-bell-count">
              ${unseen > 9 ? '9+' : unseen}
            </span>`}
        </button>
        ${/* GitHub issue #77. A fourth BALL in this box, which is what the
              comment above allows: `.brand` still sees two children, and
              `.brand-controls` is a flex row with a gap that does not care
              how many. Beside the cog rather than in each screen, for the
              reason that comment gives about the bell. */''}
        <${KeepPage} />
        <button class="ball" data-testid="brand-account"
                title="You and settings"
                aria-label="You and settings" style="color:var(--bg)"
                onClick=${() => onGo('settings')}>
          <${Icon} name="settings" size=${16} />
        </button>
      </div>
    </div>

    <nav class="tabs">
      ${TABS.map((t) => html`
        <button key=${t.screen} data-testid=${`tab-${t.screen}`}
                aria-current=${screen === t.screen ? 'page' : undefined}
                onClick=${() => onGo(t.screen)}>
          <${Icon} name=${t.icon} size=${16} />
          <span>${t.label}</span>
        </button>`)}
    </nav>

    <div class="screen">${children}</div>`;
}

/**
 * The rating, as a dial.
 *
 * Three quarters of a circle, because a full ring reads as a progress bar and
 * a rating is not progress towards anything. The scale is 800–2200: wide
 * enough that a pub player never pins the needle, narrow enough that a real
 * change is visible.
 */
export function Gauge({ value, delta }) {
  const R = 72;
  const circumference = 2 * Math.PI * R;
  const arc = circumference * 0.75;
  const pct = Math.min(1, Math.max(0, (value - 800) / 1400));
  const rising = delta >= 0;

  return html`
    <div class="gauge" data-testid="gauge">
      ${/* viewBox, not fixed pixels: the box is sized in rem so that it grows
            with the text size, and the ring has to grow with it. Drawn at 180
            units whatever it ends up being on screen. */''}
      <svg viewBox="0 0 180 180" width="100%" height="100%"
           style="transform:rotate(135deg)" aria-hidden="true">
        <circle cx="90" cy="90" r=${R} fill="none" stroke="var(--line)"
                stroke-width="10" stroke-dasharray=${`${arc} ${circumference}`}
                stroke-linecap="round" />
        <circle cx="90" cy="90" r=${R} fill="none" stroke="var(--brass)"
                stroke-width="10" stroke-dasharray=${`${arc} ${circumference}`}
                stroke-dashoffset=${circumference * (1 - pct * 0.75)}
                stroke-linecap="round"
                style="filter:drop-shadow(0 0 6px rgb(201 162 39 / .4))" />
      </svg>
      <div class="inner">
        <div class="figure gold" data-testid="gauge-value">${Math.round(value)}</div>
        <div class="cap">ELO RATING</div>
        ${delta !== null && html`
          <div class=${`delta ${rising ? 'chalk' : 'dangerc'}`}
               data-testid="gauge-delta">
            <${Icon} name=${rising ? 'trending-up' : 'trending-down'} size=${12} />
            <span>${`${rising ? '+' : ''}${Math.round(delta)}`}</span>
          </div>`}
      </div>
    </div>`;
}
